import { Controller } from "@hotwired/stimulus"

export default class extends Controller {
  static targets = ["mapContainer"]
  static values = {
    fieldId: String,
    address: String,
    latitude: String,
    longitude: String
  }

  connect() {
    // Guardar referencia global para que openMapModalDomicilioEdit pueda llamar métodos
    window.mapSelectorControllerDomicilioEdit = this;
    // Esperar a que Google Maps esté disponible
    this.waitForGoogleMaps()
    this.setupEventListeners()
  }

  setupEventListeners() {
    const confirmBtn = document.getElementById("confirm-location-btn-domicilio-edit")
    if (confirmBtn) {
      confirmBtn.addEventListener("click", () => this.confirmLocation())
    }
  }

  waitForGoogleMaps() {
    if (typeof google !== 'undefined' && google.maps) {
      this.initMap()
    } else if (window.googleMapsError) {
      console.error('Google Maps API no disponible');
      this.showApiKeyError()
    } else {
      setTimeout(() => this.waitForGoogleMaps(), 100)
    }
  }

  showApiKeyError() {
    const mapContainer = this.mapContainerTarget
    mapContainer.innerHTML = `
      <div style="display: flex; align-items: center; justify-content: center; height: 100%; background-color: #f8d7da; color: #721c24; padding: 20px; border-radius: 4px;">
        <div style="text-align: center;">
          <h4 style="margin: 0 0 10px 0;"><i class="fa-solid fa-triangle-exclamation"></i> Error: Google Maps no configurado</h4>
          <p style="margin: 0;">La clave de API de Google Maps no está configurada.</p>
          <p style="margin: 10px 0 0 0; font-size: 0.9rem;">
            Contacta al administrador para configurar la variable de entorno <code style="background: rgba(0,0,0,0.1); padding: 2px 4px; border-radius: 2px;">GOOGLE_MAP_KEY</code>
          </p>
        </div>
      </div>
    `
  }

  initMap() {
    // Coordenadas iniciales: Argentina (Buenos Aires)
    const defaultCenter = { lat: -34.6037, lng: -58.3816 }
    
    // Si hay coordenadas anteriores, usarlas
    const center = {
      lat: this.latitudeValue ? parseFloat(this.latitudeValue) : defaultCenter.lat,
      lng: this.longitudeValue ? parseFloat(this.longitudeValue) : defaultCenter.lng
    }

    this.latitude = center.lat
    this.longitude = center.lng

    this.map = new google.maps.Map(this.mapContainerTarget, {
      zoom: 15,
      center: center,
      streetViewControl: false,
      fullscreenControl: false
    })

    // Crear el pin/marcador
    this.marker = new google.maps.Marker({
      position: center,
      map: this.map,
      draggable: true,
      title: "Tu ubicación"
    })

    // Permitir que el usuario haga clic en el mapa para mover el pin
    this.map.addListener('click', (e) => {
      this.updateMarkerPosition(e.latLng)
    })

    // Actualizar coordenadas cuando se arrastra el marcador
    this.marker.addListener('dragend', () => {
      const latLng = this.marker.getPosition()
      this.latitude = latLng.lat()
      this.longitude = latLng.lng()
      this.updateCoordinates(latLng)
    })

    // Inicializar la búsqueda de direcciones
    this.initPlacesAutocomplete()

    // Mostrar coordenadas iniciales
    if (this.latitudeValue && this.longitudeValue) {
      this.updateCoordinates(center)
    }
  }

  initPlacesAutocomplete() {
    const addressInput = document.getElementById('address-input-domicilio-edit')
    if (!addressInput) return

    let sessionToken = new google.maps.places.AutocompleteSessionToken()

    // Escuchar cambios en el input
    addressInput.addEventListener('input', (e) => {
      const value = e.target.value
      
      if (value.length < 2) {
        return
      }

      // Usar Places Autocomplete
      const service = new google.maps.places.AutocompleteService()
      
      service.getPlacePredictions(
        {
          input: value,
          sessionToken: sessionToken,
          componentRestrictions: { country: 'ar' }, // Restricción a Argentina
          types: ['address']
        },
        (predictions, status) => {
          if (status === google.maps.places.PlacesServiceStatus.OK && predictions) {
            // Obtener detalles del primer resultado sugerido
            if (predictions.length > 0) {
              const prediction = predictions[0]
              
              // Obtener detalles del lugar
              const placesService = new google.maps.places.PlacesService(this.map)
              placesService.getDetails(
                {
                  placeId: prediction.place_id,
                  sessionToken: sessionToken,
                  fields: ['formatted_address', 'geometry']
                },
                (place, status) => {
                  if (status === google.maps.places.PlacesServiceStatus.OK && place.geometry) {
                    const location = place.geometry.location
                    this.updateMarkerPosition(location)
                    this.map.setCenter(location)
                    
                    // Generar nuevo session token para la próxima búsqueda
                    sessionToken = new google.maps.places.AutocompleteSessionToken()
                  }
                }
              )
            }
          }
        }
      )
    })
  }

  updateMarkerPosition(latLng) {
    this.marker.setPosition(latLng)
    this.map.setCenter(latLng)
    this.updateCoordinates(latLng)
  }

  updateCoordinates(latLng) {
    const lat = latLng.lat()
    const lng = latLng.lng()
    
    this.latitude = lat
    this.longitude = lng

    // Actualizar la visualización
    const coordsDisplay = document.getElementById('coords-display-domicilio-edit')
    coordsDisplay.textContent = `${lat.toFixed(7)}, ${lng.toFixed(7)}`

    // Llamar a la API Geocoding para obtener la dirección
    const geocoder = new google.maps.Geocoder()
    geocoder.geocode({ location: latLng }, (results, status) => {
      if (status === google.maps.GeocoderStatus.OK && results[0]) {
        const addressInput = document.getElementById('address-input-domicilio-edit')
        addressInput.value = results[0].formatted_address
      }
    })
  }

  searchAddress(event) {
    const addressInput = document.getElementById('address-input-domicilio-edit')
    const address = addressInput.value
    
    if (!address) {
      alert('Por favor ingresa una dirección')
      return
    }

    const geocoder = new google.maps.Geocoder()
    geocoder.geocode({ address: address, componentRestrictions: { country: 'ar' } }, (results, status) => {
      if (status === google.maps.GeocoderStatus.OK && results[0]) {
        const location = results[0].geometry.location
        this.updateMarkerPosition(location)
      } else {
        alert('Dirección no encontrada. Por favor intenta con otra dirección.')
      }
    })
  }

  confirmLocation(event) {
    if (!this.latitude || !this.longitude) {
      alert('Por favor selecciona una ubicación en el mapa');
      return
    }

    // Guardar las coordenadas en el campo oculto del formulario
    const fieldId = this.element.getAttribute('data-map-selector-domicilio-edit-field-id') || 'domicilio_geo_code';
    const geoCodeField = document.getElementById(fieldId)
    
    if (geoCodeField) {
      // Formato: "latitud,longitud"
      const geoCode = `${this.latitude},${this.longitude}`
      geoCodeField.value = geoCode
    }

    // Actualizar el color del botón manualmente
    const mapBtn = document.getElementById('open-map-btn-domicilio-edit');
    if (mapBtn && geoCodeField && geoCodeField.value) {
      mapBtn.innerHTML = '<i class="fa-solid fa-map-location-dot"></i> Mapa <i class="fa-solid fa-check"></i>';
      mapBtn.style.backgroundColor = '#048848';
      mapBtn.style.borderColor = '#048848';
    }

    // Cerrar el modal (mantenerlo en el DOM, solo ocultar visualmente)
    const mapaModal = document.getElementById('map-modal-wrapper-domicilio-edit');
    if (mapaModal) {
      mapaModal.classList.remove('visible');
    }
  }

  centerOnCity(cityName) {
    if (!cityName || !this.map) return;

    const geocoder = new google.maps.Geocoder();
    geocoder.geocode(
      { 
        address: `${cityName}, Argentina`,
        componentRestrictions: { country: 'AR' }
      },
      (results, status) => {
        if (status === google.maps.GeocoderStatus.OK && results[0]) {
          const location = results[0].geometry.location;
          this.map.setCenter(location);
          this.map.setZoom(13);

          if (!this.latitude || !this.longitude) {
            this.updateMarkerPosition(location);
          }
        }
      }
    );
  }

  centerOnAddress(fullAddress, cityName, street, number, latitude, longitude) {
    if (!this.map) return;

    // Si hay coordenadas exactas desde el backend, usarlas
    if (latitude && longitude) {
      const lat = parseFloat(latitude);
      const lng = parseFloat(longitude);
      
      if (!isNaN(lat) && !isNaN(lng)) {
        const location = new google.maps.LatLng(lat, lng);
        
        // Destruir el mapa anterior si existe
        if (this.map) {
          google.maps.event.clearInstanceListeners(this.map);
          this.map = null;
        }
        if (this.marker) {
          this.marker.setMap(null);
          this.marker = null;
        }
        
        // Reinicializar el mapa con las nuevas coordenadas
        this.map = new google.maps.Map(this.mapContainerTarget, {
          zoom: 16,
          center: location,
          streetViewControl: false,
          fullscreenControl: false
        });
        
        // Actualizar almacenamiento de coordenadas
        this.latitude = lat;
        this.longitude = lng;
        
        // Recrear el marcador
        this.marker = new google.maps.Marker({
          position: location,
          map: this.map,
          draggable: true,
          title: "Tu ubicación"
        });
        
        // Reattach listeners
        this.map.addListener('click', (e) => {
          this.updateMarkerPosition(e.latLng);
        });

        this.marker.addListener('dragend', () => {
          const latLng = this.marker.getPosition();
          this.latitude = latLng.lat();
          this.longitude = latLng.lng();
          this.updateCoordinates(latLng);
        });

        // Reinitialize autocomplete
        this.initPlacesAutocomplete();
        
        // Update display
        this.updateCoordinates(location);
        return;
      }
    }

    // Intentar geocodificar con dirección completa
    if (fullAddress && fullAddress.trim()) {
      const geocoder = new google.maps.Geocoder()
      geocoder.geocode({ address: fullAddress, componentRestrictions: { country: 'AR' } }, (results, status) => {
        if (status === google.maps.GeocoderStatus.OK && results[0]) {
          const location = results[0].geometry.location
          this.map.setCenter(location)
          this.map.setZoom(16)
          this.marker.setPosition(location)
          this.updateMarkerPosition(location)
          return
        }

        // Fallback: geocodificar solo la ciudad
        if (cityName && cityName.trim()) {
          this.centerOnCity(cityName)
        }
      })
      return
    }

    // Último fallback: solo ciudad
    if (cityName && cityName.trim()) {
      this.centerOnCity(cityName)
    }
  }

  disconnect() {
    // Limpiar referencia global
    if (window.mapSelectorControllerDomicilioEdit === this) {
      delete window.mapSelectorControllerDomicilioEdit
    }
  }
}
